<script setup lang="ts">
    import { onMounted, inject, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import Loading from '@shared/components/Loading.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import { UserAccess } from './User.access';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import { LanguagesAccess } from '@shared/Root/Languages.access';
    import Modal from '@shared/components/Modal.vue';
    import List from '@shared/components/List/List.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import AddAccountToUser from './AddAccountToUser.vue';
    import UserPendingDevices from './UserPendingDevices.vue';
    import { AccountListItem } from './AccountReference';

    const router = useRouter();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const userAccess: UserAccess = injectorAccess.injector.get(UserAccess);
    const languagesAccess: LanguagesAccess = injectorAccess.injector.get(LanguagesAccess);

    const props = defineProps<{
        userId: string;
    }>();

    const backRouteLabels: Record<string, string> = {
        UserList: 'Users',
        AccountEdit: 'Account',
    };

    const backRouteName = window.history.state?.back ? router.resolve(window.history.state.back).name?.toString() : undefined;
    const backLabel = (backRouteName && backRouteLabels[backRouteName]) || 'Users';

    function goBack(): void {
        if (window.history.state?.back) router.back();
        else router.push({ name: 'UserList' });
    }

    const state = reactive<{
        userForm: any;
        userFormReady: boolean;
        showPasswordModal: boolean;
        newPassword: string;
        newPasswordValid: boolean;
        showAddAccountModal: boolean;
        addingAccount: boolean;
    }>({
        userForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        userFormReady: false,
        showPasswordModal: false,
        newPassword: '',
        newPasswordValid: false,
        showAddAccountModal: false,
        addingAccount: false,
    });

    onMounted(async () => {
        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();

            languagesAccess.getLanguages().then((languages) => {
                state.userForm.data.LanguageId.select.options = languages.map((language) => ({
                    value: language.LanguageId.toString(),
                    label: language.Name,
                }));
            });
        });
    });

    function setupUserFormData(): void {
        state.userForm.data = {
            FirstName: {
                modelValue: userAccess.state.user!.FirstName,
                group: 'User',
                required: false,
                input: {
                    label: 'First Name',
                    type: 'text',
                },
            },
            LastName: {
                modelValue: userAccess.state.user!.LastName,
                group: 'User',
                required: false,
                input: {
                    label: 'Last Name',
                    type: 'text',
                },
            },
            Email: {
                modelValue: userAccess.state.user!.Email,
                group: 'User',
                required: true,
                input: {
                    label: 'Email',
                    type: 'email',
                },
            },
            MobilePhone: {
                modelValue: userAccess.state.user!.MobilePhone,
                group: 'User',
                required: false,
                input: {
                    label: 'Mobile Phone',
                    type: 'text',
                },
            },
            Status: {
                modelValue: userAccess.state.user!.Status,
                group: 'User',
                required: true,
                select: {
                    label: 'Status',
                    options: [
                        { value: '0', label: 'Enabled' },
                        { value: '1', label: 'Disabled' },
                        { value: '2', label: 'Pending Activation' },
                    ],
                },
            },
            LanguageId: {
                modelValue: userAccess.state.user!.LanguageId,
                group: 'User',
                required: true,
                select: {
                    label: 'Language',
                    options: [],
                },
            },
            PersonNummer: {
                modelValue: userAccess.state.user!.Personnummer,
                group: 'User',
                required: false,
                input: {
                    label: 'Personnummer',
                    type: 'text',
                },
            },
            Created: {
                modelValue: userAccess.state.user!.Created,
                group: 'Info',
                required: false,
                input: {
                    label: 'Created',
                    type: 'date',
                    disabled: true,
                },
            },
            LastLogin: {
                modelValue: userAccess.state.user!.LastLogin,
                group: 'Info',
                required: false,
                input: {
                    label: 'Last Login',
                    type: 'date',
                    disabled: true,
                },
            },
            ConsecutiveFailedLogins: {
                modelValue: userAccess.state.user!.ConsecutiveFailedLogins,
                group: 'Info',
                required: false,
                input: {
                    label: 'Consecutive Failed Logins',
                    type: 'number',
                    disabled: true,
                },
            },
            LastAccountName: {
                modelValue: userAccess.state.user!.LastAccountName,
                group: 'Info',
                required: false,
                input: {
                    label: 'Account',
                    type: 'text',
                    disabled: true,
                },
            },
            Data: {
                modelValue: userAccess.state.user!.Data,
                group: 'Other',
                required: false,
                textArea: {
                    label: 'Data',
                },
            },
        };

        state.userFormReady = true;
    }

    async function saveUser() {
        if (!state.userFormReady) return;

        state.userForm.setInitData = false;

        let user = userAccess.state.user!;
        const userData = state.userForm.data;

        user.FirstName = userData.FirstName.modelValue;
        user.LastName = userData.LastName.modelValue;
        user.Email = userData.Email.modelValue;
        user.MobilePhone = userData.MobilePhone.modelValue;
        user.Status = userData.Status.modelValue;
        user.LanguageId = userData.LanguageId.modelValue;
        user.Personnummer = userData.PersonNummer.modelValue;
        user.Data = userData.Data.modelValue;

        const success = await userAccess.saveUser(user);
        if (success) state.userForm.setInitData = true;
    }

    function openPasswordModal() {
        state.newPassword = '';
        state.newPasswordValid = false;
        state.showPasswordModal = true;
    }

    function updateNewPassword(newVal: string, validate: () => boolean) {
        state.newPassword = newVal;
        state.newPasswordValid = validate();
    }

    async function setPassword() {
        if (state.newPassword && state.newPasswordValid) {
            await userAccess.setPassword(props.userId, state.newPassword);
            state.showPasswordModal = false;
            state.newPassword = '';
        }
    }

    async function removeAccount(accountId: string) {
        await userAccess.removeAccount(props.userId, accountId);
        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();
        });
    }

    async function setDefaultAccount(accountId: string) {
        await userAccess.setDefaultAccount(props.userId, accountId);
        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();
        });
    }

    function openAddAccountModal() {
        state.showAddAccountModal = true;
        focusAddAccountSearchInput();
    }

    function focusAddAccountSearchInput(): void {
        setTimeout(() => {
            document.getElementById('add-account-list-search')?.focus();
        }, 50);
    }

    async function addAccount(account: AccountListItem) {
        state.addingAccount = true;
        await userAccess.addUserToAccount(props.userId, account.AccountId).finally(() => {
            state.addingAccount = false;
            state.showAddAccountModal = false;
        });

        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();
        });
    }

    const getUserName = (): string => {
        const user = userAccess.state.user;
        if (!user) return '';
        const name = `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim();
        return name.length > 0 ? name : '[No name]';
    };
</script>

<template>
    <p class="fw-600 m-lg-t m-xs-b">
        <a href="javascript:void(0)" role="button" @click="goBack">
            <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light" aria-hidden="true"></span>
            {{ backLabel }}
        </a>
        <template v-if="backLabel !== 'Users'">
            |
            <router-link :to="{ name: 'UserList' }">All users</router-link>
        </template>
    </p>

    <Loading :show="userAccess.state.loadingUser" loadingStyle="progress-bar" fixed></Loading>

    <div v-if="userAccess.state.user">
        <div v-if="!userAccess.state.loadingUser">
            <div class="m-lg-b">
                <h2 class="h3">
                    <i class="fa fa-square" :class="userAccess.getStatusClass(userAccess.state.user.StatusName)" aria-hidden="true"></i>
                    {{ getUserName() }}
                    <small class="m-xs-l">
                        <router-link
                            v-if="userAccess.state.user.DefaultAccountId"
                            :to="{ name: 'AccountEdit', params: { accountId: userAccess.state.user.DefaultAccountId } }">
                            {{ userAccess.state.user.LastAccountName }}
                        </router-link>
                    </small>
                </h2>

                <p>
                    {{ userAccess.state.user.Email }}
                </p>

                <CopyButton buttonText="Copy user id" :textToCopy="userAccess.state.user.UserId" :tooltip="userAccess.state.user.UserId" />

                <button @click="openPasswordModal" class="btn btn-default btn-sm m-xs-l" :disabled="userAccess.state.settingPassword">Set Password</button>
            </div>

            <tabs navClass="tabs list-unstyled">
                <tab title="User">
                    <h3 class="m-lg-b">User</h3>

                    <Form
                        v-if="state.userFormReady"
                        class="fieldset-grid"
                        :data="state.userForm.data"
                        :submitting="userAccess.state.savingUser"
                        :setInitData="state.userForm.setInitData"
                        @update:isValid="(key, newVal) => (state.userForm!.data[key].isValid = newVal)"
                        @update:modelValue="(key, newVal) => (state.userForm.data[key].modelValue = newVal)"
                        @onSubmit="saveUser"></Form>
                </tab>
                <tab title="Accounts">
                    <h3 class="m-lg-b">Accounts</h3>

                    <List
                        displayAs="table"
                        itemId="AccountId"
                        :items="userAccess.state.user.Accounts"
                        :tableHeaders="['Account', 'Client Company', 'Role', '']"
                        :filterKeys="['Name', 'ClientCompany.Name', 'UserAccountRoleName']"
                        :orderOptions="[
                            { key: 'Name', display: 'Account' },
                            { key: 'ClientCompany.Name', display: 'Client Company' },
                            { key: 'UserAccountRoleName', display: 'Role' },
                        ]">
                        <template #toolbar>
                            <button class="btn btn-primary" @click="openAddAccountModal">Add to account</button>
                        </template>
                        <template #item="{ item: account, index: index }">
                            <td>
                                <router-link :to="{ name: 'AccountEdit', params: { accountId: account.AccountId } }">
                                    {{ account.Name }}
                                    <span v-if="account.AccountId == userAccess.state.user.DefaultAccountId">(Default)</span>
                                </router-link>
                            </td>
                            <td>
                                {{ account.ClientCompany.Name }}
                            </td>
                            <td>{{ account.UserAccountRoleName }}</td>
                            <td class="autofit">
                                <BSDropdownButton menuClass="dropdown-menu-right" :closeOnClickInside="true" btnClass="btn-sm">
                                    <template #toggle>
                                        <span class="glyphicon glyphicon-option-vertical"></span>
                                        <span class="sr-only">{{ $Resources.NavbarMoreButton }}</span>
                                    </template>
                                    <template #content>
                                        <li>
                                            <a href="javascript:void(0)" role="button" @click="removeAccount(account.AccountId)">Remove</a>
                                        </li>
                                        <li v-if="account.AccountId !== userAccess.state.user.DefaultAccountId">
                                            <a href="javascript:void(0)" role="button" @click="setDefaultAccount(account.AccountId)">Set default</a>
                                        </li>
                                    </template>
                                </BSDropdownButton>
                            </td>
                        </template>
                    </List>
                </tab>
                <tab title="Devices">
                    <h3 class="m-lg-b">Devices</h3>

                    <UserPendingDevices :userId="props.userId" />
                </tab>
            </tabs>
        </div>
    </div>

    <Modal :show="state.showPasswordModal" @close="() => (state.showPasswordModal = false)" title="Set Password" size="sm">
        <template #content>
            <Loading :show="userAccess.state.settingPassword" loadingStyle="progress-bar"></Loading>
            <FormInput
                :disabled="userAccess.state.settingPassword"
                id="newPassword"
                label="New Password"
                type="newPassword"
                :modelValue="state.newPassword"
                @update:modelValue="updateNewPassword" />
        </template>
        <template #button>
            <button
                type="button"
                class="btn btn-primary"
                @click="setPassword"
                :disabled="userAccess.state.settingPassword || !state.newPassword || !state.newPasswordValid">
                Save
            </button>
        </template>
    </Modal>

    <Modal
        v-if="userAccess.state.user"
        :show="state.showAddAccountModal"
        @close="() => (state.showAddAccountModal = false)"
        title="Add user to account"
        size="lg">
        <template #content>
            <AddAccountToUser :user-accounts="userAccess.state.user.Accounts" @add-account="addAccount" />
        </template>
    </Modal>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
